import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, Signal, Plane, Globe, Users, BookOpen, Sparkles, PlaneTakeoff, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { cleanCourseList, buildHeroSlides, getDisplayTitle, getCourseImage } from '../../lib/landingCourses';
import { localeUpperCase } from '../../lib/textCase';
import useReducedMotion from '../../hooks/useReducedMotion';
import TrustMarquee from './TrustMarquee';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTOPLAY_MS = 6500;

/**
 * 3 sabit tema slaytı — her biri kendi arka plan rengi + kendi metni + kendi
 * 2 kursu ile birlikte gelir (landing v2 hero, edX/Harvard tarzı redesign).
 * Renkler ui-ux WCAG AA doğrulaması yapılmış spec'ten; değiştirilmemeli.
 */
const SLIDES = [
  { key: 'slide1', bg: '#0B1A33', hasNote: false },
  { key: 'slide2', bg: '#0F3D2E', hasNote: true },
  { key: 'slide3', bg: '#3B1220', hasNote: false },
];

const CATEGORY_ICON = {
  cabin_crew: Plane,
  language: Globe,
  business: Users,
  hr_training: Users,
  programming: Sparkles,
};

function CategoryIcon({ category, ...props }) {
  const Icon = CATEGORY_ICON[category] || BookOpen;
  return <Icon {...props} />;
}

function categoryLabelKey(category) {
  if (category === 'cabin_crew') return 'landing2.trending.tab.aviation';
  if (category === 'language') return 'landing2.trending.tab.languages';
  return 'landing2.trending.tab.career';
}

function HeroCardSkeleton() {
  return (
    <div className="flex-shrink-0 snap-start w-[85%] sm:w-[70%] md:w-auto md:flex-1 md:min-w-[220px] bg-white/10 animate-pulse rounded-lg aspect-[3/4]" />
  );
}

function HeroCardEmpty({ t, showTestId }) {
  return (
    <div
      className="flex-1 min-w-[220px] rounded-lg border border-white/15 bg-white/5 py-10 px-6 text-center flex flex-col items-center gap-3"
      data-testid={showTestId ? 'hero-slider-empty' : undefined}
    >
      <PlaneTakeoff size={32} className="text-[#F7F5F0]/40" aria-hidden="true" />
      <p className="text-[#F7F5F0]/70 font-semibold text-sm">{t('landing2.hero.emptyTitle')}</p>
    </div>
  );
}

/**
 * Kart görsel kabuğu (rozet şeridi + görsel + ikon rozeti) — real/curated
 * ortak. `data-testid` çağıran taraftan `...rest` ile literal olarak
 * geçirilir (bkz. HeroCard/HeroCuratedCard) — statik test dosyası
 * (heroSliderStatic.test.js) `data-testid={showTestId ? '...' : undefined}`
 * biçimindeki TAM ternary metnini kaynak kodda arıyor.
 */
function HeroCardShell({ to, category, image, loadImage, t, children, ...rest }) {
  return (
    <Link
      to={to}
      className="flex-shrink-0 snap-start w-[85%] sm:w-[70%] md:w-auto md:flex-1 md:min-w-[220px] bg-white rounded-lg overflow-hidden flex flex-col hover:-translate-y-1 transition-transform"
      style={{ boxShadow: '0 14px 32px rgba(0,0,0,.28)' }}
      {...rest}
    >
      <div
        className="flex items-center text-[11px] font-extrabold truncate"
        style={{ height: 34, padding: '8px 14px', background: '#E8E3D9', color: '#0B1A33', letterSpacing: '0.04em' }}
      >
        {t(categoryLabelKey(category))}
      </div>
      <div className="relative bg-[#0E1F3D] overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
        {loadImage && (
          <img
            src={image}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <div
          className="absolute flex items-center justify-center bg-white"
          style={{ top: 10, left: 10, width: 34, height: 34, borderRadius: 8 }}
          aria-hidden="true"
        >
          <CategoryIcon category={category} size={18} strokeWidth={2.2} className="text-[#0B1A33]" aria-hidden="true" />
        </div>
      </div>
      {/* Reviewer bulgusu #1 (kart hizasi): `hero-course-slider` container'i
          align-items:stretch ile kartlari esitliyor (satir ~320). Rozet
          kaldirildiktan sonra HeroCuratedCard (2 satir) HeroCard'dan (4 satir)
          daha kisa icerik render ediyordu, stretch nedeniyle kisa kartin
          ALTINDA kalici bos alan olusuyordu. Sahte ders sayisi/seviye EKLEMEK
          yerine bu icerik sarmalayicisi flex-1 yapilip dikey ortalanir — Link
          artik `flex flex-col`, boylece bu son cocuk stretch'ten gelen fazla
          yuksekligi devralir ve kisa icerik (curated) kutunun ortasina,
          uzun icerik (real, 4 satir) ise dogal olarak neredeyse tamamini
          kaplayarak hizalanir. */}
      <div className="flex-1 flex flex-col justify-center" style={{ padding: 16 }}>{children}</div>
    </Link>
  );
}

/**
 * Gerçek (DB) kurs kartı — `/courses/:id` detayına bağlanır. `loadImage=false`
 * iken <img> hiç `src` almaz (reviewer bulgusu #2 — 3 panel de aynı
 * viewport'ta stack'li olduğu için native `loading="lazy"` etkisiz
 * kalıyordu; artık sadece aktif olmuş/olan slaytların görselleri gerçekten
 * indirilir).
 */
function HeroCard({ course, t, lang, getField, showTestId, loadImage }) {
  return (
    <HeroCardShell
      to={`/courses/${course.course_id}`}
      data-testid={showTestId ? 'hero-course-card' : undefined}
      category={course.category}
      image={getCourseImage(course)}
      loadImage={loadImage}
      t={t}
    >
      <h3 className="text-[17px] font-extrabold leading-[1.25] line-clamp-2 text-[#0E1F3D]">
        {getDisplayTitle(course, lang, getField)}
      </h3>
      <div className="text-[13px] font-semibold mt-1 mb-3.5" style={{ color: '#46564c' }}>
        {t('landing2.common.academyLabel')}
      </div>
      <div className="flex items-center gap-1.5" style={{ color: '#46564c' }}>
        <Clock size={14} aria-hidden="true" />
        <span className="text-[13px] font-semibold">{course.lessons_count || 0} {t('landing2.common.lessonsSuffix')}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5" style={{ color: '#46564c' }}>
        <Signal size={14} aria-hidden="true" />
        <span className="text-[13px] font-semibold">{t(course.level) || course.level}</span>
      </div>
    </HeroCardShell>
  );
}

/**
 * Küratörlü (DB'de karşılığı olmayan) kart — `card.to` üzerinden gerçek ve
 * doğrulanmış bir kategori/listeye bağlanır (kırık link yok). Uydurma ders
 * sayısı/seviye YOK — hero'daki diğer kartlarla aynı sadelikte (başlık +
 * sağlayıcı adı) render edilir, rozet yok.
 */
function HeroCuratedCard({ card, t, showTestId, loadImage }) {
  return (
    <HeroCardShell
      to={card.to}
      data-testid={showTestId ? 'hero-curated-card' : undefined}
      category={card.category}
      image={card.image}
      loadImage={loadImage}
      t={t}
    >
      <h3 className="text-[17px] font-extrabold leading-[1.25] line-clamp-2 text-[#0E1F3D]">
        {t(card.titleKey)}
      </h3>
      <div className="text-[13px] font-semibold mt-1" style={{ color: '#46564c' }}>
        {t('landing2.common.academyLabel')}
      </div>
    </HeroCardShell>
  );
}

export default function HeroSlider() {
  const { t, lang, getField } = useLanguage();
  const reduced = useReducedMotion();
  const [pool, setPool] = useState(null); // null = ilk yükleme
  const [error, setError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // Reviewer bulgusu #1 (YÜKSEK, WCAG 2.2.2): hover ve focus önceden AYNI
  // `paused` boolean'ını paylaşıyordu — Tab ile CTA'ya odaklanıp fare
  // hero'dan çıkarsa onMouseLeave koşulsuz resume() çağırıyor, odak
  // içerideyken autoplay devam ediyordu. Artık iki state ayrı tutuluyor.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const paused = hovered || focused;
  // Reviewer bulgusu #2: bir slayt en az bir kez aktif olduysa görselleri
  // "ziyaret edilmiş" say — bir daha inaktif olsa da <img src> kaldırılmaz
  // (gereksiz yeniden indirme olmaz), ama HENÜZ hiç aktif olmamış slaytların
  // görselleri ilk aktivasyona kadar hiç indirilmez.
  const [visited, setVisited] = useState(() => new Set([0]));

  useEffect(() => {
    let cancelled = false;
    // /courses/featured backend'de limit=8 sabit; demo/test filtresi bu
    // limit'ten SONRA uygulanirsa gercek kurslar hic cekilmeden atilabilir.
    // Bunun yerine /courses?sort=popular genis bir limit'le (30) cekilir,
    // temizlenir, sonra 3 slayta dagitilir (bkz. buildHeroSlides).
    axios.get(`${API}/courses?sort=popular&limit=30`)
      .then((res) => {
        if (cancelled) return;
        setPool(cleanCourseList(res.data?.courses || []));
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setPool([]);
      });
    return () => { cancelled = true; };
  }, []);

  const slideCourseSets = pool ? buildHeroSlides(pool) : [[], [], []];

  useEffect(() => {
    if (reduced || paused) return undefined;
    const timer = setInterval(() => {
      setActiveIndex((i) => (i + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [reduced, paused]);

  useEffect(() => {
    setVisited((prev) => {
      if (prev.has(activeIndex)) return prev;
      const next = new Set(prev);
      next.add(activeIndex);
      return next;
    });
  }, [activeIndex]);

  const goPrev = useCallback(() => setActiveIndex((i) => (i - 1 + SLIDES.length) % SLIDES.length), []);
  const goNext = useCallback(() => setActiveIndex((i) => (i + 1) % SLIDES.length), []);
  const goTo = useCallback((i) => setActiveIndex(i), []);
  const pause = useCallback(() => setHovered(true), []);
  const resumeHover = useCallback(() => setHovered(false), []);
  const pauseFocus = useCallback(() => setFocused(true), []);
  const resumeFocus = useCallback(() => setFocused(false), []);

  const activeSlide = SLIDES[activeIndex];
  const loading = pool === null && !error;

  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: activeSlide.bg, transition: reduced ? 'none' : 'background-color 400ms ease-out' }}
      data-testid="landing2-hero"
      role="region"
      aria-roledescription={t('landing2.hero.nav.carouselRoleDesc')}
      aria-label={t(`landing2.hero.${activeSlide.key}.eyebrow`)}
      onMouseEnter={pause}
      onMouseLeave={resumeHover}
      onFocus={pauseFocus}
      onBlur={resumeFocus}
    >
      <div className="landing2-hero-rays" aria-hidden="true" />

      {/* Üstte fixed/overlay header için boşluk — Navbar.js landing'de saydam
          olup hero üzerine bindiği için başlık header'ın ALTINDA kalmasın.
          Alt sınır (min-height) 3 slaytın en uzununa göre eşitlenir (madde 3)
          ve üst sınır laptop viewport'una (1366x768/1440x900) sığacak şekilde
          `svh` tabanlı clamp() ile ölçeklenir (madde 2). */}
      <div className="relative max-w-6xl mx-auto px-6 pt-[clamp(86px,15svh,108px)] pb-[clamp(20px,3svh,40px)]">
        {/* min-height mobilde (grid-cols-1, metin+kartlar ALT ALTA dizilir)
            ve lg+'da (grid-cols-2, yan yana) FARKLI bütçelere ihtiyaç duyar —
            mobilde slide2'nin ekstra "note" satırı yığılmış toplam yüksekliğe
            ~32px daha ekliyor, bu yüzden mobil taban tüm slaytların en
            uzununu (slide2) kapsayacak kadar yüksek tutulur. lg+'da kartlar
            her zaman 2 tane olduğu için (aynı en-boy oranı) doğal yükseklik
            zaten neredeyse eşit; küçük clamp bandı sadece güvence payı. */}
        <div className="relative min-h-[clamp(700px,90svh,820px)] lg:min-h-[clamp(300px,40svh,380px)]">
          {SLIDES.map((slide, i) => {
            const isActive = i === activeIndex;
            const courses = slideCourseSets[i];
            const loadImages = isActive || visited.has(i);
            return (
              <div
                key={slide.key}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center transition-opacity duration-300 ${
                  isActive ? 'opacity-100 relative' : 'opacity-0 absolute inset-0 pointer-events-none'
                }`}
                role="group"
                aria-roledescription={t('landing2.hero.nav.slideRoleDesc')}
                aria-label={`${i + 1} / ${SLIDES.length}`}
                aria-hidden={!isActive}
                inert={!isActive}
              >
                <motion.div
                  key={`text-${slide.key}-${isActive}`}
                  initial={reduced || !isActive ? undefined : { opacity: 0, y: 12 }}
                  animate={reduced || !isActive ? undefined : { opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
                >
                  <div className="inline-flex items-center gap-2 border border-[#C9A227]/40 bg-[#C9A227]/10 text-[#C9A227] text-xs font-bold tracking-widest px-3.5 py-1.5 rounded-full">
                    {localeUpperCase(t(`landing2.hero.${slide.key}.eyebrow`), lang)}
                  </div>
                  <h1
                    className="landing2-display landing2-hero-title text-[clamp(2.1rem,7svh,4.5rem)] text-[#F7F5F0] mt-4"
                    data-testid={isActive ? 'hero-title' : undefined}
                  >
                    {t(`landing2.hero.${slide.key}.titleLine1`)}<br />
                    <span className="text-[#C9A227]">{t(`landing2.hero.${slide.key}.titleHighlight`)}</span>
                  </h1>
                  <p className="text-[1.1rem] leading-[1.5] text-[#c5cfdd] mt-4 max-w-none md:max-w-[50ch]">
                    {t(`landing2.hero.${slide.key}.body`)}
                  </p>
                  <div className="flex flex-wrap gap-3.5 mt-6">
                    <motion.div whileTap={reduced ? undefined : { scale: 0.96 }}>
                      <Link
                        to="/courses"
                        className="inline-flex items-center gap-2 bg-[#C9A227] text-[#241c02] text-base font-extrabold px-8 py-4 rounded-full hover:brightness-105 hover:-translate-y-0.5 transition-all shadow-[0_10px_24px_rgba(201,162,39,.25)]"
                        data-testid={isActive ? 'hero-get-started-btn' : undefined}
                      >
                        {t(`landing2.hero.${slide.key}.cta`)}
                        <ArrowRight size={18} aria-hidden="true" />
                      </Link>
                    </motion.div>
                  </div>
                  {slide.hasNote && (
                    <p className="text-sm text-[#F7F5F0]/80 mt-3">
                      {t(`landing2.hero.${slide.key}.note`)}
                    </p>
                  )}
                </motion.div>

                <div
                  className="flex gap-5 overflow-x-auto snap-x snap-mandatory md:overflow-visible md:snap-none"
                  data-testid={isActive ? 'hero-course-slider' : undefined}
                >
                  {loading && (
                    <>
                      <HeroCardSkeleton />
                      <HeroCardSkeleton />
                    </>
                  )}
                  {!loading && courses.length < 2 && <HeroCardEmpty t={t} showTestId={isActive} />}
                  {!loading && courses.length >= 2 && courses.map((slot) => (
                    slot.type === 'real' ? (
                      <HeroCard
                        key={slot.course.course_id}
                        course={slot.course}
                        t={t}
                        lang={lang}
                        getField={getField}
                        showTestId={isActive}
                        loadImage={loadImages}
                      />
                    ) : (
                      <HeroCuratedCard
                        key={slot.card.key}
                        card={slot.card}
                        t={t}
                        showTestId={isActive}
                        loadImage={loadImages}
                      />
                    )
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center mt-6">
          <div
            className="flex items-center gap-4 sm:gap-4 px-1.5"
            style={{ height: 56, padding: 6, borderRadius: 999, background: '#FFFFFF', boxShadow: '0 8px 20px rgba(11,26,51,.15)' }}
          >
            <button
              type="button"
              onClick={goPrev}
              className="flex items-center justify-center rounded-full transition-colors"
              style={{ width: 44, height: 44, color: '#0B1A33' }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#0B1A33'; e.currentTarget.style.color = '#FFFFFF'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0B1A33'; }}
              aria-label={t('landing2.hero.nav.prevSlide')}
              data-testid="hero-slider-prev"
            >
              <ChevronLeft size={18} aria-hidden="true" />
            </button>

            <div className="flex gap-2">
              {SLIDES.map((slide, i) => {
                const isActive = i === activeIndex;
                return (
                  <button
                    key={slide.key}
                    type="button"
                    onClick={() => goTo(i)}
                    className="p-2 -m-2 flex items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0B1A33]"
                    aria-label={`${t('landing2.hero.nav.goToSlideLabel')} ${i + 1}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <span
                      className="block rounded-full"
                      style={{
                        width: isActive ? 24 : 8,
                        height: 8,
                        background: isActive ? '#C9A227' : '#0B1A33',
                        border: isActive ? '1.5px solid #0B1A33' : 'none',
                        transition: reduced ? 'none' : 'width 260ms ease, background 260ms ease',
                      }}
                    />
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={goNext}
              className="flex items-center justify-center rounded-full transition-colors"
              style={{ width: 44, height: 44, color: '#0B1A33' }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#0B1A33'; e.currentTarget.style.color = '#FFFFFF'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#0B1A33'; }}
              aria-label={t('landing2.hero.nav.nextSlide')}
              data-testid="hero-slider-next"
            >
              <ChevronRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <TrustMarquee />
    </section>
  );
}
